import { type ReactNode } from 'react';

/**
 * Matches an http(s):// or www. URL. Deliberately greedy to the next
 * whitespace (which includes a newline, so a match never spans lines); trailing punctuation is trimmed afterwards by
 * {@link trimTrailingPunctuation}, which is easier to reason about than
 * encoding sentence punctuation into the pattern.
 */
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<]+/gi;

/** Sentence punctuation that is never part of a URL when it ends one. */
const TRAILING_PUNCTUATION = /[.,:;!?'"’”]+$/;

const WWW_PREFIX = /^www\./i;

/**
 * Drops punctuation that belongs to the sentence rather than to the URL, so
 * "see https://example.com." links the address and leaves the full stop as
 * text. A closing bracket is only dropped when the URL has no matching
 * opener, which keeps paths like /foo(bar) intact.
 */
const BRACKET_PAIRS = [
  ['(', ')'],
  ['[', ']'],
  ['{', '}'],
] as const;

const countOf = (text: string, character: string): number => text.split(character).length - 1;

const trimTrailingPunctuation = (url: string): string => {
  let trimmed = url.replace(TRAILING_PUNCTUATION, '');

  for (const [open, close] of BRACKET_PAIRS) {
    while (trimmed.endsWith(close) && countOf(trimmed, open) < countOf(trimmed, close)) {
      trimmed = trimmed.slice(0, -1);
    }
  }

  return trimmed.replace(TRAILING_PUNCTUATION, '');
};

/**
 * Splits the text into plain runs and anchors. Anchor markup mirrors Stream's
 * own `Anchor` renderer, so a link in a literally rendered message is styled
 * and secured exactly like one in a markdown-rendered message.
 */
const linkify = (text: string): ReactNode[] => {
  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0;
    const url = trimTrailingPunctuation(match[0]);
    if (!url) continue;

    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <a
        key={start}
        className="str-chat__message-url-link"
        href={WWW_PREFIX.test(url) ? `https://${url}` : url}
        rel="nofollow noreferrer noopener"
        target="_blank"
      >
        {url}
      </a>
    );
    cursor = start + url.length;
  }

  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

/**
 * Renders a chat message exactly as it was typed.
 *
 * Chat message text is stored as markdown (IP-1290, IP-1918), but this app's
 * composer is a plain textarea — a client cannot produce markdown on purpose,
 * so parsing their prose only ever deletes characters they meant: `*heel*`
 * loses its asterisks, `~~niet~~` renders struck through and inverts the
 * meaning, a leading `-` disappears, and the sentence after a list is absorbed
 * into the last bullet. Nothing here parses, escapes or rewrites the text
 * (IP-2192).
 *
 * Line breaks are preserved and URLs are still clickable, neither of which
 * changes a character of what was written. Mentions are not rendered: they
 * cannot be composed in this app, and a mention plugin would have to match
 * against the text and alter it.
 *
 * Matches Stream's `renderText` signature so it can be handed to
 * `MessageSimple`, ignoring the mentioned-users and options arguments.
 */
export const renderLiteralText = (text?: string): ReactNode => {
  if (!text) return null;

  // `pre-wrap` rather than <br> elements, and set inline rather than left to
  // either app's stylesheet: a <br> is not part of `textContent`, so a reader
  // copying the message out lost its line breaks, and under the default
  // `white-space` a run of spaces collapsed. Both change the characters the
  // client sent. A <p> also matches what the markdown path emits, so the two
  // kinds of message share the surrounding typography.
  return <p style={{ whiteSpace: 'pre-wrap' }}>{linkify(text)}</p>;
};
